import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, ArrowLeft, Mail, Phone, MapPin, Lock, Search } from "lucide-react";
import { AdminMasterclass } from "./AdminMasterclass";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState(["Da contattare", "In corso", "Chiusa"]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [savingId, setSavingId] = useState(null);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (user) return;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (e) {
        setUser(null);
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [user]);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    setLoadingLeads(true);
    axios
      .get(`${API}/admin/leads`, { withCredentials: true })
      .then((res) => {
        setLeads(res.data.leads);
        if (res.data.statuses) setStatuses(res.data.statuses);
      })
      .catch(() => setError("Impossibile caricare le richieste."))
      .finally(() => setLoadingLeads(false));
  }, [user]);

  const changeStatus = async (id, status) => {
    setSavingId(id);
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, { withCredentials: true });
    } catch (e) {
      setError("Impossibile aggiornare lo stato.");
    } finally {
      setSavingId(null);
    }
  };

  const statusColor = (s) =>
    s === "Chiusa"
      ? "bg-espresso text-crema border-espresso"
      : s === "In corso"
      ? "bg-fuchsiabrand text-white border-fuchsiabrand"
      : "bg-transparent text-stone border-espresso/30";

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
    window.location.href = "/";
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-crema">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone">Verifica…</p>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-crema px-5">
        <div className="max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-fuchsiabrand flex items-center justify-center mx-auto mb-6">
            <Lock className="text-white" size={24} />
          </div>
          <h1 className="font-display uppercase text-fuchsiabrand text-3xl sm:text-4xl mb-4">
            Area Riservata
          </h1>
          <p className="text-stone mb-8">
            Accedi con Google per vedere tutte le richieste ricevute dal sito.
          </p>
          <button
            onClick={login}
            data-testid="google-login-button"
            className="w-full bg-espresso text-crema py-4 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-fuchsiabrand transition-colors duration-300"
          >
            Accedi con Google
          </button>
          <a href="/" className="mt-6 inline-flex items-center gap-2 text-stone hover:text-fuchsiabrand text-sm transition-colors">
            <ArrowLeft size={16} /> Torna al sito
          </a>
        </div>
      </div>
    );
  }

  // Logged in but not the owner
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-crema px-5">
        <div className="max-w-md w-full text-center">
          <h1 className="font-display uppercase text-espresso text-3xl mb-4">Accesso negato</h1>
          <p className="text-stone mb-8">
            L'account <span className="text-espresso font-semibold">{user.email}</span> non è
            autorizzato a vedere quest'area.
          </p>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="bg-fuchsiabrand text-white py-3 px-6 rounded-full font-mono text-xs uppercase tracking-[0.15em]"
          >
            Esci
          </button>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-crema">
      <header className="border-b border-espresso/10 bg-crema/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="font-display text-lg text-espresso leading-none">Richieste</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone mt-1">
              Alina Cossali · Area Riservata
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="hidden sm:inline-flex items-center gap-2 text-stone hover:text-fuchsiabrand text-sm transition-colors">
              <ArrowLeft size={16} /> Sito
            </a>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="inline-flex items-center gap-2 bg-espresso text-crema px-4 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.15em] hover:bg-fuchsiabrand transition-colors duration-300"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
        {/* Tabs */}
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {[
            { id: "leads", label: "Richieste" },
            { id: "masterclass", label: "Guida Digitale" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.15em] border transition-all duration-300 ${
                tab === t.id
                  ? "bg-espresso text-crema border-espresso"
                  : "bg-transparent text-stone border-espresso/20 hover:border-fuchsiabrand"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "masterclass" ? (
          <AdminMasterclass />
        ) : (
        <>
        <div className="flex items-baseline gap-3 mb-8">
          <span className="font-display text-5xl text-fuchsiabrand">{leads.length}</span>
          <span className="text-stone">richieste ricevute</span>
        </div>

        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, email, città o messaggio…"
              data-testid="admin-search"
              className="w-full bg-white/50 border border-espresso/15 rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-fuchsiabrand transition-colors"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", ...statuses].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`px-4 py-2.5 rounded-full text-xs border transition-all duration-300 ${
                  statusFilter === s
                    ? "bg-espresso text-crema border-espresso"
                    : "bg-transparent text-stone border-espresso/20 hover:border-fuchsiabrand"
                }`}
              >
                {s === "all" ? "Tutte" : s}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-fuchsiabrand mb-4">{error}</p>}
        {loadingLeads && <p className="text-stone">Caricamento…</p>}

        {(() => {
          const q = search.trim().toLowerCase();
          const filtered = leads.filter((l) => {
            const matchesStatus = statusFilter === "all" || (l.status || "Da contattare") === statusFilter;
            const hay = `${l.name} ${l.email} ${l.city} ${l.message} ${l.intent_label}`.toLowerCase();
            return matchesStatus && (!q || hay.includes(q));
          });

          if (!loadingLeads && filtered.length === 0) {
            return <p className="text-stone" data-testid="admin-empty">Nessuna richiesta trovata.</p>;
          }

          return (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="leads-list">
              {filtered.map((l) => (
                <div
                  key={l.id}
                  className="border border-espresso/10 bg-white/40 p-6"
                  data-testid="lead-card"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="font-display text-lg text-espresso">{l.name}</p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-fuchsiabrand border border-fuchsiabrand/40 rounded-full px-3 py-1">
                      {l.intent_label}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm text-stone">
                    <p className="flex items-center gap-2">
                      <Mail size={14} className="text-fuchsiabrand" />
                      <a href={`mailto:${l.email}`} className="hover:text-fuchsiabrand">{l.email}</a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone size={14} className="text-fuchsiabrand" />
                      <a
                        href={`https://wa.me/${(l.phone || "").replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-fuchsiabrand"
                      >
                        {l.phone}
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin size={14} className="text-fuchsiabrand" />
                      {l.city}
                    </p>
                  </div>
                  {l.message && (
                    <p className="mt-4 pt-4 border-t border-espresso/10 text-sm text-espresso/80 italic">
                      “{l.message}”
                    </p>
                  )}

                  {/* Status controls */}
                  <div className="mt-4 pt-4 border-t border-espresso/10">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone/70 mb-2">Stato</p>
                    <div className="flex gap-2 flex-wrap">
                      {statuses.map((s) => {
                        const activeS = (l.status || "Da contattare") === s;
                        return (
                          <button
                            key={s}
                            onClick={() => changeStatus(l.id, s)}
                            disabled={savingId === l.id}
                            data-testid={`lead-status-${s}`}
                            className={`px-3 py-1.5 rounded-full text-[11px] border transition-all duration-300 disabled:opacity-50 ${
                              activeS ? statusColor(s) : "bg-transparent text-stone border-espresso/20 hover:border-fuchsiabrand"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-stone/70">
                    {new Date(l.timestamp).toLocaleString("it-IT")}
                  </p>
                </div>
              ))}
            </div>
          );
        })()}
        </>
        )}
      </main>
    </div>
  );
}
